import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Profiling with React DevTools
//  Chapter 8 · Performance
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `// 1. Open Chrome DevTools → React tab → Profiler
// 2. Click the Record button (circle icon)
// 3. Interact with your app (click, type, navigate)
// 4. Stop recording
// 5. Analyse the flame chart

// What to look for:
// - Components with high render time (wide bars)
// - Components re-rendering unnecessarily (grey bars in commit view)
// - Waterfalls — components waiting on each other

// Profiler API — measure specific interactions in code
import { Profiler } from 'react';

function onRenderCallback(
  id,           // Component tree identifier
  phase,        // 'mount' or 'update'
  actualDuration,  // ms to render
  baseDuration,    // estimated ms without memoization
  startTime,
  commitTime
) {
  if (actualDuration > 16) {  // > 16ms = drops below 60fps
    console.warn(\`⚠️ Slow render in \${id}: \${actualDuration.toFixed(2)}ms\`);
  }
}

function App() {
  return (
    <Profiler id="ProductList" onRender={onRenderCallback}>
      <ProductList />
    </Profiler>
  );
}`;

const CODE_2 = `// Problem: Component renders on every parent update
// Fix: React.memo + useCallback
const ExpensiveChild = memo(({ data, onAction }) => { /* ... */ });
const handleAction = useCallback(() => { /* ... */ }, []);

// Problem: Expensive calculation runs every render
// Fix: useMemo
const processedData = useMemo(() => expensiveProcess(rawData), [rawData]);

// Problem: Large list re-renders all items on scroll
// Fix: Virtualisation with react-window
import { FixedSizeList as List } from 'react-window';

function VirtualList({ items }) {
  const Row = ({ index, style }) => (
    <div style={style}><ItemCard item={items[index]} /></div>
  );
  return (
    <List height={600} itemCount={items.length} itemSize={80} width="100%">
      {Row}
    </List>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function ProfilingWithReactDevTools() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 8 · Performance</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Profiling with React DevTools</h1>
        <p style={styles.desc}>Identify and fix performance bottlenecks using the React DevTools Profiler.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Setting Up React DevTools ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Setting Up React DevTools</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Install the React Developer Tools browser extension from the Chrome Web Store or Firefox Add-ons. It adds two tabs to your browser DevTools: <b>Components</b> (inspect component tree, props, state) and <b>Profiler</b> (measure rendering performance)." }} />
      </div>

      /* ── Using the Profiler ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Using the Profiler</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "The Profiler records render timings during interactions. It shows which components rendered, why they rendered, and how long they took." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Common Performance Fixes ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Common Performance Fixes</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "After profiling, apply targeted optimisations:" }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What is the difference between actual duration and base duration in the Profiler?</div>
          <div style={styles.qItem}>✦ 2. What does it mean when a component shows a grey bar in the commit view?</div>
          <div style={styles.qItem}>✦ 3. How do you identify which prop change caused a component to re-render?</div>
          <div style={styles.qItem}>✦ 4. What is virtualisation and when should you use it?</div>
          <div style={styles.qItem}>✦ 5. Profile a 500-item list and optimise it using React.memo, useCallback, and react-window.</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
